import { usePatientConditionsAll } from '@ctw/shared/api/fhir/conditions';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { applyFilters, type OverviewFilter } from '@ctw/shared/content/overview/filters';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { orderBy } from 'lodash-es';
import { lastUpdatedMostRecentConditionSort } from './sorts';
import { statusView } from './views';

export const filters = (): Array<OverviewFilter<ConditionModel>> => [
  {
    key: 'conditions',
    label: 'chronic conditions or acute conditions with hcc',
    description: 'chronic conditions or acute conditions with hcc',
    predicate: (r: ConditionModel) =>
      r.chronic?.code === 'C' || (r.chronic?.code === 'A' && r.hasHCCv28Codes),
  },
];

export interface PatientConditionsOverviewData {
  data: Array<ConditionModel>;
  isLoading: boolean;
  hasNoRecords: boolean;
}

/**
 * Fetches, filters, and sorts the conditions shown in the patient overview card.
 * Returns the ready-to-render list along with `hasNoRecords`, which lets callers
 * distinguish "no records at all" from "records exist but none match the filters".
 */
export function usePatientConditionsOverviewData(): PatientConditionsOverviewData {
  const { current } = statusView;
  const { data, isLoading } = usePatientConditionsAll();

  const { data: filteredData } = useFilteredSortedData({
    defaultSort: lastUpdatedMostRecentConditionSort,
    viewOptions: [current],
    records: data,
    defaultView: current.display,
  });

  const filteredSortedData = orderBy(
    applyFilters(filteredData, filters()),
    (d) => d.display,
    'asc',
  );

  return {
    data: filteredSortedData,
    isLoading,
    hasNoRecords: data.length === 0,
  };
}
